// ============================================
// Captain Model Integration Pipeline Types — CAP.08
//
// Stage 3 (Final) of the biomarker data lifecycle.
//
// Schema chain:
//   RawExtractedBiomarker (CAP.04/CAP.05 — raw names, pre-aliasing)
//   → AliasedBiomarker (CAP.02 — canonical names + processing flags)
//   → RawBiomarkerResult → BiomarkerResult (CAP.08 — clinical ranges + status)
//
// AliasedBiomarker.canonicalName → BiomarkerResult.name
// AliasedBiomarker.flag (lab reference) → BiomarkerResult.status
//   (functional range classification — these are DIFFERENT systems, not aliases)
//
// Use Cases (Spec Gate):
//   UC-1: First-time lab upload with successful extraction
//   UC-2: Partial extraction with missing biomarkers
//   UC-3: Unit conversion and validation
//   UC-4: Captain model failure with graceful degradation
//   UC-5: Phase 1b model transition (Claude Opus → MedGemma 27B)
// ============================================

// ── Schema Versioning ──

/**
 * Schema version for all CAP.08 interfaces.
 * Consumers MUST check schemaVersion and reject unknown versions
 * rather than silently parsing.
 * Bump on any breaking field change.
 */
export const CAPTAIN_SCHEMA_VERSION = '1.0.0';

import type { DateExtractionResult } from './dateExtraction';

// ── Pipeline Input ──

/**
 * Request to the Captain model integration pipeline.
 *
 * UC-1: Patient uploads clear lab PDF → CaptainRequest created with
 *       image URL + patient/session IDs.
 * UC-4: If model fails, pipeline returns hard error — no fabricated results.
 */
export interface CaptainRequest {
  /** URL to uploaded lab image (pre-signed, 15-min TTL) */
  imageUrl: string;
  /** Firestore patient document ID */
  patientId: string;
  /** Current analysis session ID */
  sessionId: string;
  /** Request hostname (for mock detection, passed to CAP.9) */
  hostname: string;
}

// ── Model Extraction Output (Pre-Standardization) ──

/**
 * A single biomarker result as extracted by the model, before
 * unit standardization or range application.
 *
 * UC-2: If critical markers are missing from the extracted set,
 *       pipeline throws hard error with specific missing marker names.
 * UC-3: Unit field here is the raw extracted unit — may need conversion.
 */
export interface RawBiomarkerResult {
  /** Biomarker name as extracted by model (pre-standardization) */
  name: string;
  /** Raw value string from model (e.g., "4.2", "<0.5", ">100") */
  rawValue: string;
  /** Unit as extracted from lab */
  unit: string;
  /** 0-1 extraction confidence from model */
  confidence: number;
  /** Where in the document this was extracted from */
  sourceLocation?: {
    /** Page number in the source document */
    page: number;
    /** Approximate region descriptor (e.g., "top-third", "table-row-5") */
    region: string;
  };
}

/**
 * Envelope for the raw model extraction output.
 *
 * UC-4: On model failure (HTTP 500, timeout, malformed response),
 *       this is never fabricated — hard error is thrown instead.
 * UC-5: modelVersion tracks which model produced the extraction
 *       for quality comparison during Phase 1b transition.
 */
export interface RawExtractionResult {
  /** Array of extracted biomarker results */
  biomarkers: RawBiomarkerResult[];
  /** Model version string (e.g., "claude-opus-4-20250514") */
  modelVersion: string;
  /** Timestamp of extraction */
  extractionTimestamp: Date;
  /** SHA-256 of model raw response for audit trail */
  rawResponseHash: string;
  /** Any fields model returned that couldn't be parsed */
  parseErrors: string[];
}

// ── Pipeline Output (Post-Standardization) ──

/**
 * A single biomarker result after unit standardization, range
 * application, and status classification.
 *
 * This is the terminal biomarker schema — consumed by CaptainResponse
 * and handed off via CAP.17 to downstream bones (Nurse, Protocol).
 *
 * UC-3: value and unit are post-conversion; originalUnit preserves
 *       the lab's original unit for audit trail.
 */
export interface CaptainBiomarkerResult {
  /** Standardized biomarker name (e.g., "TSH") */
  name: string;
  /** Canonical ID from biomarkerCatalog (e.g., "tsh") */
  canonicalId: string;
  /** Numeric value after unit standardization */
  value: number;
  /** Standardized unit (e.g., "mIU/L") */
  unit: string;
  /** Unit as extracted from lab (before conversion) */
  originalUnit: string;
  /** Lab reference range */
  standardRange: { min: number; max: number };
  /** Dr. Calapai validated range (null if unavailable) */
  functionalRange: { min: number; max: number } | null;
  /** Which range was used for status classification */
  rangeSource: 'functional' | 'standard' | 'fallback';
  /** Clinical status classification based on active range */
  status: 'optimal' | 'suboptimal' | 'deficient' | 'high';
  /** 0-1 from Captain model extraction */
  confidence: number;
}

/**
 * Full response from the Captain model integration pipeline.
 *
 * UC-1: On success, contains structured biomarker array with
 *       validated units, functional range classifications.
 * UC-2: Branch A — partial results with missingCritical empty,
 *       non-critical gaps noted in extractionStats.
 *       Branch B — hard error thrown, CaptainResponse not returned.
 * UC-4: On model failure, CaptainResponse is not returned —
 *       hard error with logging and PagerDuty alert instead.
 * UC-5: captainModelVersion tracks which model produced results
 *       for quality comparison during transition.
 */
export interface CaptainResponse {
  /** CAPTAIN_SCHEMA_VERSION — consumers reject unknown versions */
  schemaVersion: string;
  /** Whether extraction and processing succeeded */
  success: boolean;
  /** Array of processed biomarker results */
  biomarkers: CaptainBiomarkerResult[];
  /** Extraction statistics and quality metrics */
  extractionStats: {
    /** Number of biomarkers successfully extracted */
    totalFound: number;
    /** Total possible biomarkers for this lab type */
    totalPossible: number;
    /** Critical biomarkers that were not found (UC-2 Branch A: informational only) */
    missingCritical: string[];
    /** Markers excluded by CAP.13 confidence gating */
    lowConfidenceMarkers: string[];
  };
  /** Demographics used for range application */
  demographicsStatus: {
    /** Where demographics were sourced from */
    source: 'profile' | 'lab-report' | 'universal';
    /** Profile vs lab report mismatch detected */
    demographicsConflict: boolean;
    /** No demographics available; universal ranges used */
    demographicsUnknown: boolean;
    /** Calculated from DOB on file at extraction time */
    ageAtExtraction: number;
  };
  /** Wall clock milliseconds for full pipeline processing */
  processingTime: number;
  /** Model that produced the extraction (e.g., "claude-opus-4-20250514" or "medgemma-27b-v1") */
  captainModelVersion: string;
  /** Downstream handoff status, managed by CAP.17 */
  handoffStatus: 'pending' | 'delivered' | 'failed';
  /**
   * Lab date extraction metadata — CAP.12.
   * Flight Attendant MUST consume this field. See flightAttendantDateContract.ts
   * for mandatory behaviors. Null only during schema migration transition.
   */
  dateExtraction: DateExtractionResult | null;
}

// ── Model Abstraction Layer ──

/**
 * Abstraction for swappable model providers.
 *
 * UC-5: Both Claude Opus and MedGemma implement this interface,
 *       enabling zero-downtime model swaps, A/B testing during
 *       Phase 1b transition, and fallback to previous model.
 *
 * UC-4: 30-second timeout enforced per extract() call.
 *       No automatic retry on model errors — model errors indicate
 *       systemic issues, not transient blips.
 */
export interface CaptainModelProvider {
  /** Provider name: "claude-opus" | "medgemma-27b" */
  name: string;
  /** Provider version string */
  version: string;
  /** Extract biomarkers from a redacted lab image */
  extract(redactedImage: Buffer, prompt: string): Promise<RawExtractionResult>;
  /** Check if the model provider is healthy and available */
  healthCheck(): Promise<boolean>;
}

// ── Dependency Service Contracts ──
// Stubs are replaced with real implementations before any patient
// data is processed. Each has a hard gate documented in the spec.

/**
 * PII Redaction Service interface contract (HLX-C06).
 *
 * Dev stub: returns image unchanged with empty redaction log.
 * Hard gate: stub must be replaced before any real patient data
 * enters the pipeline.
 */
export interface PIIRedactionService {
  /** Redact PII from a lab image before model processing */
  redactImage(imageUrl: string, sessionId: string, callId: string): Promise<{
    /** URL to the redacted image */
    redactedImageUrl: string;
    /** Log of all terms redacted */
    redactionLog: { term: string; type: string; confidence: number }[];
  }>;
  /** Restore redacted tokens in text output */
  restoreTokens(text: string, sessionId: string, callId: string): Promise<string>;
}

/**
 * Unit Validation Service interface contract (C11 / CAP.6).
 *
 * Dev stub: accepts top 50 units from hardcoded lookup table,
 * rejects all others. Common conversion factors hardcoded.
 * Hard gate: full unit database required before launch.
 */
export interface UnitValidationService {
  /** Validate and optionally convert a biomarker's unit */
  validate(value: number, unit: string, biomarkerName: string): Promise<{
    /** Whether the unit is recognized and valid */
    isValid: boolean;
    /** The standardized unit after conversion */
    standardizedUnit: string;
    /** The value after conversion (same if no conversion needed) */
    convertedValue: number;
    /** Whether a unit conversion was applied */
    conversionApplied: boolean;
    /** Confidence in the validation/conversion */
    confidence: 'high' | 'low' | 'ambiguous';
  }>;
}

/**
 * Range Validation Service interface contract (C10 / CAP.10).
 *
 * Dev stub: returns PLUG values with isPlugValue: true for all biomarkers.
 * Pre-launch gate: 0 PLUG values in production.
 * Hard gate: Dr. Calapai validation required before launch.
 */
export interface RangeValidationService {
  /** Get standard and functional ranges for a biomarker */
  getRanges(biomarkerName: string, demographics: { age: number; sex: string }): Promise<{
    /** Standard (lab) reference range */
    standardRange: { min: number; max: number };
    /** Functional (Dr. Calapai validated) range, null if unavailable */
    functionalRange: { min: number; max: number } | null;
    /** Whether this range is a placeholder (PLUG) value */
    isPlugValue: boolean;
    /** Source of the range data */
    source: 'dr-calapai' | 'plug' | 'published-reference';
  }>;
}

/**
 * Confidence Gating Service interface contract (CAP.13).
 *
 * Dev stub: passes all results with confidence >= 0.7, excludes below.
 * Hard gate: threshold must be calibrated against gold standard before launch.
 */
export interface ConfidenceGatingService {
  /** Apply confidence threshold to extraction results */
  applyThreshold(results: RawBiomarkerResult[]): Promise<{
    /** Results that passed the confidence threshold */
    passed: RawBiomarkerResult[];
    /** Results excluded with reason */
    excluded: { result: RawBiomarkerResult; reason: string }[];
  }>;
}

// ── Downstream Handoff (CAP.17) ──

/**
 * Extraction status for CaptainResultDocument.
 *
 * UC-1: 'complete' — all biomarkers extracted with sufficient confidence.
 * UC-2: 'partial' — some biomarkers below confidence threshold.
 * UC-3: 'failed' — extraction failed entirely (unreadable image, model failure).
 */
export type ExtractionStatus = 'complete' | 'partial' | 'failed';

/**
 * Firestore Timestamp shape — matches both admin and client SDK.
 * Use Firestore's native Timestamp when reading/writing; this type
 * ensures the interface compiles without an SDK import.
 */
export interface FirestoreTimestamp {
  readonly seconds: number;
  readonly nanoseconds: number;
  toDate(): Date;
}

/**
 * Firestore document persisted at /captain-results/{sessionId}.
 *
 * This is the contract between Captain (writer) and Nurse (reader via onSnapshot).
 * Firestore IS the handoff AND the notification — no synchronous API call,
 * no push protocol, no acknowledgment dance.
 *
 * No TTL. Results persist with the patient's account. This is clinical data,
 * not a transient message. Deletion happens only when the patient's account
 * is deleted (per data retention policy).
 *
 * Use Cases (Spec Gate):
 *   UC-1: Successful extraction → extractionStatus 'complete', full CaptainResponse.
 *         Nurse's onSnapshot fires instantly, patient sees results explained.
 *   UC-2: Partial extraction → extractionStatus 'partial', low-confidence biomarkers
 *         flagged in CaptainResponse. Nurse presents high-confidence results,
 *         notes "Some values need additional review."
 *   UC-3: Extraction fails entirely → extractionStatus 'failed', failureReason populated.
 *         Nurse tells patient: "We couldn't process your lab report."
 *   UC-4: Firestore write fails → Captain retries (built-in), then hard error to patient:
 *         "Unable to save your results. Please try again." PagerDuty alert fires.
 *   UC-5: Nurse listener disconnected → Nurse restarts, onSnapshot reconnects,
 *         Firestore replays missed changes. No dead-letter queue needed.
 */
export interface CaptainResultDocument {
  /** Current analysis session ID — also the Firestore document ID */
  sessionId: string;
  /** Firestore patient document ID */
  patientId: string;
  /** Full extraction result from Captain pipeline */
  captainResponse: CaptainResponse;
  /** Extraction completeness: complete, partial, or failed */
  extractionStatus: ExtractionStatus;
  /** Reason for failure when extractionStatus is 'failed' */
  failureReason?: string;
  /** NRS.01 D#5: Set to true when clinical sanity check detects implausible biomarker values */
  clinicalSanityFlag?: boolean;
  /** When this document was first written */
  createdAt: FirestoreTimestamp;
  /** When this document was last updated */
  updatedAt: FirestoreTimestamp;
}

// ── Pipeline Error Types ──

/**
 * Error codes for Captain pipeline failures.
 * Each maps to a specific user-visible error message.
 */
export type CaptainPipelineErrorCode =
  | 'INPUT_VALIDATION_FAILED'
  | 'MOCK_DETECTED'
  | 'PII_REDACTION_FAILED'
  | 'MODEL_EXTRACTION_FAILED'
  | 'MODEL_TIMEOUT'
  | 'MODEL_RATE_LIMITED'
  | 'MODEL_MALFORMED_RESPONSE'
  | 'UNIT_VALIDATION_FAILED'
  | 'UNIT_AMBIGUOUS'
  | 'DEMOGRAPHICS_CONFLICT'
  | 'RANGE_APPLICATION_FAILED'
  | 'CLINICAL_RULES_UNAVAILABLE'
  | 'CRITICAL_BIOMARKER_MISSING'
  | 'CRITICAL_BIOMARKER_LOW_CONFIDENCE'
  | 'INVALID_FILE';

/**
 * Structured error from the Captain pipeline.
 * Every failure includes enough context for Cloud Logging
 * and operations alerting.
 */
export interface CaptainPipelineError {
  /** Machine-readable error code */
  code: CaptainPipelineErrorCode;
  /** User-visible error message */
  message: string;
  /** Pipeline stage where the error occurred (1-11) */
  stage: number;
  /** Stage name for readability */
  stageName: string;
  /** Session ID for correlation */
  sessionId: string;
  /** Request ID for correlation */
  requestId?: string;
  /** Timestamp of the error */
  timestamp: Date;
  /** Specific details (e.g., missing marker names, ambiguous unit) */
  details?: Record<string, unknown>;
}

// ── Pipeline Stage Names ──

/**
 * Sequential pipeline stages as defined in the spec.
 * Used for error reporting and processing metrics.
 */
export const CAPTAIN_PIPELINE_STAGES = [
  'input-validation',
  'mock-detection',
  'pii-redaction',
  'model-extraction',
  'unit-validation',
  'demographics-resolution',
  'range-application',
  'confidence-gating',
  'critical-biomarker-validation',
  'response-assembly',
  'downstream-handoff',
] as const;

export type CaptainPipelineStage = typeof CAPTAIN_PIPELINE_STAGES[number];
